import React from 'react'
import { useMutation, useQuery } from 'react-apollo'
import { useHistory } from 'react-router-dom'
import useServerError from '../../../src/hooks/useServerError'
import RegisterInvitationMutation from '../../graphql/RegisterInvitationMutation.gql'
import ValidInvitationTokenQuery from '../../graphql/ValidInvitationTokenQuery.gql'
import { Form, FormIdentifier } from '../forms/FieldFactory'
import FormBuilder from '../forms/FormBuilder'
import { Helmet } from 'react-helmet'
import {
  BasicType,
  ElementType,
} from '../../../src/components/forms/FormElementParts'
import { getQueryParamValue } from '../../../src/lib/uri'

export interface Props {
  forms: Form[]
}

const tenant_config = (window as any).CMD.config.tenant

const informed_consent_and_privacy = {
  label: `I agree to the  <a href='${tenant_config.informed_consent}'>Patient Informed Consent</a> and the <a href='${tenant_config.notice_of_privacy_practices}'>Notice of Privacy Practices</a>`,
  name: 'agreeToInformedConsentAndPrivacyPractices',
  type: ElementType.CHECKBOX,
}

const patient_financial_responsibility = {
  label: `I agree to the  <a href='${tenant_config.patient_financial_responsibility}'>Patient Financial Responsibility Agreement</a>`,
  name: 'agreeToFinancialResponsibility',
  type: ElementType.CHECKBOX,
}

const term_fields = {
  fields: [
    {
      label:
        '<h2>Terms and Agreements</h2> Your privacy and consent are important to us. Please select each box to indicate you agree.',
      type: BasicType.PLAINTEXT,
    },
    {
      label: `I agree to the <a href='${tenant_config.terms_of_service}'>Terms of Use</a> and <a href='${tenant_config.privacy_policy}'>Privacy Policy</a>`,
      name: 'agreeToTerms',
      type: ElementType.CHECKBOX,
    },
    ...(tenant_config.informed_consent &&
    tenant_config.notice_of_privacy_practices
      ? [informed_consent_and_privacy]
      : []),
    ...(tenant_config.patient_financial_responsibility
      ? [patient_financial_responsibility]
      : []),
    {
      label: `By checking this box, I authorize ${tenant_config.customer_name} and its affiliates and service providers including but not limited to CMDPN, LLC, to contact me at the phone number provided through my membership or profile via SMS text message using automated dialing technology for marketing and/or advertising purposes. I understand that I do not have to grant consent to use ${tenant_config.customer_name}'s program and that message and data rates may apply. To opt-out or revoke consent reply STOP to any SMS text message.`,
      required: false,
      name: 'agreeToTextMessageMarketingAgreement',
      type: ElementType.CHECKBOX,
    },
    {
      label: 'Continue',
      type: BasicType.SUBMIT,
    },
    {
      label: "Already have an account? <a href='/sign_in'>Sign In</a>",
      type: BasicType.FOOTER,
    },
  ],
  header: 'Create Account',
  type: FormIdentifier.REGISTER_INVITATION,
}

const password_fields = {
  fields: [
    {
      icon: 'Key',
      label: 'Password',
      name: 'password',
      type: ElementType.PASSWORD,
    },
    {
      icon: 'Key',
      label: 'Password Confirmation',
      name: 'passwordConfirmation',
      type: ElementType.PASSWORD,
    },
    {
      label: 'Create Account',
      type: BasicType.SUBMIT,
    },
  ],
  header: 'please create and confirm your password below',
  type: FormIdentifier.REGISTER_INVITATION,
}

const password_and_term_fields = {
  fields: [
    {
      icon: 'Key',
      label: 'Password',
      name: 'password',
      type: ElementType.PASSWORD,
    },
    {
      icon: 'Key',
      label: 'Password Confirmation',
      name: 'passwordConfirmation',
      type: ElementType.PASSWORD,
    },
    {
      label: 'I have read and agree to the terms and conditions',
      name: 'agreeToTerms',
      type: ElementType.SWITCH,
    },

    {
      label: 'Get Started',
      type: BasicType.SUBMIT,
    },
  ],
  header: 'please create and confirm your password below',
  type: FormIdentifier.REGISTER_INVITATION,
}

export const forms = () => {
  if ((window as any).CMD.config.terms_and_agreements_enabled) {
    return [term_fields, password_fields]
  } else {
    return [password_and_term_fields]
  }
}

const RegisterInvitationPage = () => {
  const [index, setIndex] = React.useState(0)
  const form = forms()[index]
  const { clearServerError, serverError, setServerError } = useServerError()
  const token = getQueryParamValue('invitation_token')
  const { data: validTokenData } = useQuery(ValidInvitationTokenQuery, {
    variables: { token },
  })
  const history = useHistory()

  const [registerUser, { data, error }] = useMutation(
    RegisterInvitationMutation,
  )

  const handleSubmit = ({ password, passwordConfirmation, ...checkboxes }) => {
    let newIndex = index + 1
    if (index === 0) {
      setIndex(newIndex)
    }
    if (newIndex === forms().length) {
      setIndex(index)
      const termFields = [
        'agreeToTerms',
        'agreeToInformedConsentAndPrivacyPractices',
        'agreeToFinancialResponsibility',
        'agreeToTextMessageMarketingAgreement',
      ]

      const checkboxValues = {}

      termFields.forEach(field => {
        checkboxValues[field] = checkboxes[field] === 'true' || false
      })

      const input = {
        ...checkboxValues,
        password,
        passwordConfirmation,
        invitationToken: token,
      }

      registerUser({
        variables: {
          input,
        },
      })
    }
  }

  React.useEffect(() => {
    if (error && error.graphQLErrors.length) {
      const err: any = error.graphQLErrors[0]
      setServerError({ message: err.message, errors: err.fields })
    } else {
      clearServerError()
    }
  }, [clearServerError, error, setServerError])
  if (validTokenData && validTokenData.validInvitationToken) {
    const { statusCode, message } = validTokenData.validInvitationToken
    if (statusCode === 422 && message && message.length) {
      history.push('/invitation/resend?invalid_token=true', { message })
    }
  }
  if (data && data.registerInvitation) {
    window.location.href = '/'
  }
  return (
    <React.Fragment>
      <Helmet
        title={`${
          (window as any).CMD.config.tenant.page_title
        } - Resend Your Invitation`}
      />
      <FormBuilder
        form={form}
        onChange={clearServerError}
        onSubmit={handleSubmit}
        serverError={serverError}
      />
    </React.Fragment>
  )
}

export default RegisterInvitationPage
